import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  HeartOutlined,
  SearchOutlined,
  FilterOutlined,
  PictureOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Typography,
  notification,
  Upload,
  Card,
  Statistic,
  Select
} from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import ajaxService from "../../../services/ajax-service";
import { Filter } from "../../../components";
import { useHistory, Link } from "react-router-dom";
import moment from "moment";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";

const { Text, Title } = Typography;
const { Option } = Select;

const ListstyleQuestions = () => {
  const [form] = Form.useForm();

  const [filter, setFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const debounceTimer = useRef(null);
  const [showModal, setShowModal] = useState(false);

  const [dataSource, setDataSource] = useState({});
  const [additionalData, setAdditionalData] = useState({
    total_lifestyles: 0,
    with_images: 0,
    option_questions: 0
  });
  const [questionType, setQuestionType] = useState(2);
  const [options, setOptions] = useState([{ id: "new_0", option: "" }]);
  const [optionErrors, setOptionErrors] = useState([false]);
  const [editId, setEditId] = useState(null); // <-- track edit mode
  const [fileList, setFileList] = useState([]);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modalWidth, setModalWidth] = useState('35%');

  // ---------- Options Handlers ----------
  const handleAddOption = () => {
    const hasEmpty = options.some((opt) => !opt.option.trim());
    if (hasEmpty) {
      notification.warning({
        message: "Please fill all existing options before adding a new one.",
      });
      return;
    }

    const newId = `new_${Date.now()}`;
    setOptions([...options, { id: newId, option: "" }]);
    setOptionErrors([...optionErrors, false]);
  };

  const handleOptionChange = (value, index) => {
    const updatedOptions = [...options];
    updatedOptions[index].option = value;
    setOptions(updatedOptions);

    const updatedErrors = [...optionErrors];
    updatedErrors[index] = !value.trim();
    setOptionErrors(updatedErrors);
  };

  const handleRemoveOption = (index) => {
    const updatedOptions = options.filter((_, idx) => idx !== index);
    const updatedErrors = optionErrors.filter((_, idx) => idx !== index);
    setOptions(updatedOptions);
    setOptionErrors(updatedErrors);
  };

  // ---------- Table Columns ----------
  const columns = [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 80,
      fixed: 'left',
      render: (text, record, index) => (
        (dataSource.current_page - 1) * dataSource.per_page + index + 1
      ),
    },
    {
      title: "Image",
      key: "image",
      width: 100,
      render: (_, record) => (
        record.image_url ? (
          <img
            src={record.image_url}
            alt="Lifestyle Question"
            style={{ 
              width: 50, 
              height: 50, 
              objectFit: "contain",
              borderRadius: 'var(--radius-md)'
            }}
          />
        ) : (
          <span style={{ color: 'var(--text-gray-light)' }}>No Image</span>
        )
      ),
    },
    {
      title: "Lifestyle Question",
      dataIndex: "question",
      key: "question",
      width: 400,
      ellipsis: true,
      render: (question) => <Text>Q : {question}</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
      sortDirections: ['ascend', 'descend'],
      render: (created_at) => (<Text style={{ fontSize: '0.8rem' }}>{moment(created_at).format('DD/MM/YY')}</Text>)
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: 'right',
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <Link to={`/liststyle-questions/${record.id}`}>
            <Button
              type="text"
              size="small"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                color: 'var(--primary-purple)',
                border: '1px solid var(--primary-purple)',
                borderRadius: 'var(--radius-md)',
                transition: 'var(--transition-fast)'
              }}
              icon={<EyeOutlined style={{ fontSize: '12px' }} />}
            />
          </Link>
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--accent-purple)',
              border: '1px solid var(--accent-purple)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<EditOutlined style={{ fontSize: '12px' }} />}
            onClick={() => openEditModal(record)}
          />
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--error-red)',
              border: '1px solid var(--error-red)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
            onClick={() => handleDeleteClick(record.id)}
          />
        </div>
      ),
    },
  ];

  // ---------- CRUD ----------
  const handleDeleteClick = (id) => {
    setQuestionToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (questionToDelete) {
      try {
        const response = await ajaxService.delete(`/lifestyles/`, questionToDelete);

        const { success } = response;
        if (success) {
          notification.open({
            message: `Lifestyle Question deleted successfully!`,
            icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
          });
          initialize();
        } else {
          notification.open({
            message: "Unable to delete lifestyle question.",
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
          });
        }
      } catch (error) {
        console.error('Delete error:', error);
        notification.open({
          message: "Unable to delete lifestyle question.",
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    }
    setDeleteModalVisible(false);
    setQuestionToDelete(null);
  };

  const openEditModal = (record) => {
    setEditId(record.id);
    setShowModal(true);
    setQuestionType(record.options_type);
    setOriginalImageUrl(record.image_url);

    form.setFieldsValue({
      type: record.options_type,
      question: record.question,
    });

    setFileList(
      record.image_url
        ? [
            {
              uid: `-1`,
              name: "image",
              status: "done",
              url: record.image_url,
            },
          ]
        : []
    );

    let newOptions = [];
    if (record.options_type === 1) {
      if (record.question_options?.length) {
        newOptions = record.question_options.map((opt) => ({ id: opt.id, option: opt.option }));
      } else {
        const newId = `new_${Date.now()}`;
        newOptions = [{ id: newId, option: "" }];
      }
    } else {
      newOptions = [];
    }
    setOptions(newOptions);
    setOptionErrors(new Array(newOptions.length).fill(false));
  };

  const handleTypeChange = (value) => {
    const newType = Number(value);
    setQuestionType(newType);
    if (newType === 1 && options.length === 0) {
      const newId = `new_${Date.now()}`;
      setOptions([{ id: newId, option: "" }]);
      setOptionErrors([false]);
    }
  };

  const onFinish = async (values) => {
    if (questionType === 1) {
      const emptyIndexes = options
        .map((opt, i) => (!opt.option.trim() ? i : -1))
        .filter((i) => i !== -1);
      if (emptyIndexes.length > 0) {
        const updatedErrors = options.map((opt) => !opt.option.trim());
        setOptionErrors(updatedErrors);

        notification.error({ message: "Please fill in all options before submitting." });
        return;
      }
    }

    const formData = new FormData();
    formData.append("question", values.question);
    formData.append("options_type", questionType);

    if (questionType === 1) {
      const filteredOptions = options.filter((opt) => opt.option.trim() !== "");
      filteredOptions.forEach((opt, idx) => {
        formData.append(`question_options[${idx}][option]`, opt.option);
        if (opt.id && !String(opt.id).startsWith("new_")) {
          formData.append(`question_options[${idx}][id]`, opt.id);
        }
      });
    }

    const hasNewFile = fileList.some((file) => file.originFileObj);
    const shouldRemoveImage = editId && originalImageUrl && fileList.length === 0;
    const shouldKeepImage = editId && !hasNewFile && fileList.length > 0;

    if (hasNewFile) {
      const newFile = fileList.find((file) => file.originFileObj);
      formData.append("image", newFile.originFileObj);
    } 

    const response = editId
      ? await ajaxService.postFormData(`/update-lifestyle/`, editId, formData)
      : await ajaxService.post("/lifestyles", formData);

    if (response.success) {
      handleCancel();
      initialize();
      notification.open({
        message: editId ? "Question updated successfully" : response.message,
        icon: <CheckCircleOutlined style={{ color: "var(--primary-purple)" }} />,
      });
    } else {
      notification.open({
        message: "Unable to save question.",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  };

  const handleSearch = async ({ name }) => {
    const searchName = name ? name.trim() : '';
    const response = await ajaxService.get(`/lifestyles?question=${searchName}`);
    if (response.success) {
      if (response.data.data.length === 0 && searchName) {
        setFilter("");
        notification.open({
          message: `${searchName} is not found!`,
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      } else {
        setFilter(searchName);
      }
      setDataSource(response.data);
      if (response.additionalData) setAdditionalData(response.additionalData);
    }
  };

  // Debounced search handler
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      handleSearch({ name: searchValue });
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue]);

  const handleChangePage = async (page) => {
    const response = await ajaxService.get(
      `/lifestyles?question=${filter}&page=${page}`
    );
    if (response.success) {
      setDataSource(response.data);
    }
  };

  const initialize = async () => {
    const response = await ajaxService.get(`/lifestyles?question=${filter}`);
    if (response.success) {
      setDataSource(response.data);
      if (response.additionalData) setAdditionalData(response.additionalData);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  // Calculate table scroll width using standardized utility
  // Table columns total: 80 (SNo) + 100 (Image) + 400 (Question) + 150 (Actions) = 730px
  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 730, 730, 40);
  }, [sidebarCollapsed]);

  const [tableScrollX, setTableScrollX] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      return calculateTableScrollX(false, 730, 730, 40);
    }
    return '100%';
  });

  useEffect(() => {
    const handleResize = () => {
      setTableScrollX(calculateTableWidth());
      setModalWidth(calculateModalWidth('35%'));
    };

    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    handleResize(); // Initial calculation

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    };
  }, [calculateTableWidth]);

  const handleCancel = () => {
    setShowModal(false);
    setEditId(null);
    setOriginalImageUrl(null);
    setOptions([{ id: "new_0", option: "" }]);
    setOptionErrors([false]);
    setFileList([]);
    form.resetFields();
  };

  // ---------- Modal ----------
  const renderModal = (
    <Modal
      className="modal-overlay"
      width={modalWidth}
      open={showModal}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <div style={{ 
        padding: "var(--spacing-2xl) var(--spacing-xl)",
        background: 'var(--dark-bg-card)',
        borderRadius: 'var(--radius-lg)'
      }}>
        {/* Modal Header */}
        <div style={{ 
          marginBottom: 'var(--spacing-2xl)', 
          textAlign: "center",
          paddingBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
        }}>
          <Title id="modal-title" level={3} style={{ 
            color: 'var(--text-white)', 
            margin: 0,
            fontFamily: 'Poppins-Bold',
            fontSize: '1.5rem'
          }}>
            {editId ? "Edit Question" : "Add New Question"}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Select Question Type</span>}
                name="type"
                initialValue={2}
                rules={[{ required: true, message: "Please select question type" }]}
              >
                <Select
                  className="input-field"
                  placeholder="Select question type"
                  onChange={handleTypeChange}
                  style={{ width: '100%' }}
                  size="large"
                >
                  <Option value={2}>Simple Question (No Options)</Option>
                  <Option value={1}>Question With Options</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Question</span>}
                name="question"
                rules={[{ required: true, message: "Question is required" }]}
              >
                <Input 
                  addonBefore="Q " 
                  placeholder="Write question here" 
                  allowClear 
                  className="input-field"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item label={<span style={{ 
                color: 'var(--text-white)', 
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'block',
                marginBottom: 'var(--spacing-xs)'
              }}>Question Image</span>}>
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  onChange={({ fileList: newFileList }) => setFileList(newFileList)}
                  beforeUpload={() => false}
                  className="upload-field"
                >
                  {fileList.length < 1 && (
                    <div>
                      <PlusOutlined style={{ color: 'var(--text-gray)' }} />
                      <div style={{ marginTop: 8, color: 'var(--text-gray)' }}>Upload Image</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          {questionType === 1 && (
            <div style={{ 
              marginTop: 'var(--spacing-lg)',
              padding: 'var(--spacing-lg)',
              background: 'rgba(254, 2, 125, 0.05)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid rgba(254, 2, 125, 0.2)'
            }}>
              <Title level={5} style={{ 
                color: 'var(--text-white)', 
                marginBottom: 'var(--spacing-md)',
                fontFamily: 'Poppins-SemiBold',
                fontSize: '1rem'
              }}>
                Options
              </Title>
              {options.map((item, index) => (
                <Row key={item.id} gutter={8} style={{ marginBottom: 'var(--spacing-md)' }}>
                  <Col span={20}>
                    <Input
                      placeholder={`Option ${index + 1}`}
                      value={item.option}
                      onChange={(e) => handleOptionChange(e.target.value, index)}
                      className="input-field"
                      size="large"
                    />
                  </Col>
                  <Col span={4}>
                    {options.length > 1 && (
                      <Button 
                        danger 
                        onClick={() => handleRemoveOption(index)}
                        style={{
                          background: 'var(--error-red)',
                          borderColor: 'var(--error-red)',
                          width: '100%',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        icon={<DeleteOutlined />}
                      />
                    )}
                  </Col>
                </Row>
              ))}
              <Button 
                type="dashed" 
                block 
                onClick={handleAddOption}
                style={{
                  borderColor: 'rgba(254, 2, 125, 0.3)',
                  color: 'var(--text-white)',
                  marginTop: 'var(--spacing-sm)',
                  height: '40px',
                  background: 'rgba(254, 2, 125, 0.1)'
                }}
                icon={<PlusOutlined />}
              >
                Add Option
              </Button>
            </div>
          )}

          {/* Modal Footer */}
          <div style={{ 
            textAlign: "center", 
            marginTop: 'var(--spacing-2xl)',
            paddingTop: 'var(--spacing-lg)',
            borderTop: '1px solid rgba(254, 2, 125, 0.2)',
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              key="cancel"
              onClick={handleCancel}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              Cancel
            </Button>
            <Button
              key="finish"
              type="primary"
              onClick={form.submit}
              className="btn-primary"
              style={{ 
                minWidth: '120px',
                height: '40px'
              }}
            >
              {editId ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <h1 className="module-title">Lifestyle Questions</h1>
        <p className="module-subtitle">Manage lifestyle assessment questions with images</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stats-card">
          <div className="stats-value">{additionalData.total_lifestyles ?? 0}</div>
          <div className="stats-label">Total Questions</div>
          <HeartOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{additionalData.with_images ?? 0}</div>
          <div className="stats-label">With Images</div>
          <PictureOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{additionalData.option_questions ?? 0}</div>
          <div className="stats-label">Option Questions</div>
          <FilterOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-item">
            <Input
              placeholder="Search lifestyle questions..."
              prefix={<SearchOutlined style={{ color: 'var(--text-gray-light)' }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={(e) => {
                setSearchValue(e.target.value);
                handleSearch({ name: e.target.value });
              }}
              className="input-field"
              allowClear
            />
          </div>
          <div className="filter-actions">
            <Button
              className="btn-primary"
              icon={<PlusOutlined />}
              onClick={() => setShowModal(true)}
            >
              Add New Question
            </Button>
          </div>
        </div>
      </div>

      {/* Questions Table */}
      <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Table
          columns={columns}
          dataSource={dataSource.data}
          className="data-table"
          rowKey="id"
          pagination={{
            onChange: (page) => handleChangePage(page),
            current: dataSource.current_page,
            pageSize: dataSource.per_page,
            total: dataSource.total,
            className: "data-table-pagination",
            showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} questions`,
            size: 'small'
          }}
          scroll={{ x: tableScrollX }}
          size="small"
          bordered={false}
        />
      </div>

      {renderModal}
      <Modal
        className="modal-overlay"
        width={calculateModalWidth('400px')}
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        centered
      >
        <div style={{ 
          padding: "var(--spacing-2xl)",
          background: 'var(--dark-bg-card)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center'
        }}>
          <div style={{ 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '3rem',
            color: 'var(--error-red)'
          }}>
            <DeleteOutlined />
          </div>
          <Title level={4} style={{ 
            color: 'var(--text-white)', 
            marginBottom: 'var(--spacing-md)',
            fontFamily: 'Poppins-Bold'
          }}>
            Confirm Delete
          </Title>
          <p style={{ 
            color: 'var(--text-gray)', 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}>
            Are you sure you want to delete this lifestyle question? This action cannot be undone.
          </p>
          <div style={{ 
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              onClick={() => setDeleteModalVisible(false)}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirmDelete}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--error-red)',
                borderColor: 'var(--error-red)'
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ListstyleQuestions;